/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { StyleBuilder } from '../StyleBuilder';
import { VirtualCSS, textComponentsSet } from './VirtualCSS';
import {
	TABLET_BREAKPOINT,
	DESKTOP_BREAKPOINT,
	Style,
	clone,
} from '../../../constants';

const DEFAULT_UNIT = 'px';

const MOBILE_QUERY = `
/* STYLES IN MOBILE */
@media only screen and (max-width: ${TABLET_BREAKPOINT - 1}px)`;
const TABLET_QUERY = `
/* STYLES IN TABLET */
@media only screen and (min-width:${TABLET_BREAKPOINT}px) and (max-width:${DESKTOP_BREAKPOINT}px)`;
const DESKTOP_QUERY = `
/* STYLES IN DESKTOP */
@media only screen and (min-width: ${DESKTOP_BREAKPOINT + 1}px)`;

type InlineStyleValueSelector = {
	selector: string;
	styles: Array<string>;
};

export class VirtualStyleBuilder {
	articles: Array<any>;
	stylesMap: Map<string, Style>;
	styles: {
		mobile: Set<string>;
		tablet: Set<string>;
		desktop: Set<string>;
	};
	classNameMap: Map<string, string>;
	response: Array<string> = [];

	inlineStyleSelector: Map<string, InlineStyleValueSelector>;

	constructor(articles: Array<any>, stylesMap: Map<string, any>) {
		this.articles = articles;
		this.stylesMap = stylesMap;
		this.classNameMap = new Map();
		this.styles = {
			mobile: new Set(),
			tablet: new Set(),
			desktop: new Set(),
		};
		this.response = [];
		this.inlineStyleSelector = new Map();
	}

	async build(): Promise<{ css: string; classNameMap: Map<string, any> }> {
		this.response = [];
		this.inlineStyleSelector = new Map();
		this.processAdStyle();
		this.processArticlesComponentInlineStyles();
		this.processTextStyle();
		this.loadStylesFromArticle();
		this.processArticleStyle();
		this.processDeviceStyle();
		this.processInlineStyles();
		this.processCustomStyle();
		return {
			css: this.response.join('\n'),
			classNameMap: this.classNameMap,
		};
	}

	processArticleStyle() {
		this.response.push(
			[...this.styles.mobile]
				.map(this.toStyle)
				.map(toCSS)
				.filter((i) => i)
				.join('\n'),
		);
	}

	processDeviceStyle() {
		// Get device styles for tablet
		if (this.styles.tablet.size) {
			const tabletCSS = [...this.styles.tablet]
				.map(this.toStyle)
				.map(toCSS)
				.filter((i) => i)
				.join('\n');
			this.response.push(`${TABLET_QUERY} { ${tabletCSS} }`);
		}
		// Get device styles for tablet
		if (this.styles.desktop.size) {
			const desktopCSS = [...this.styles.desktop]
				.map(this.toStyle)
				.map(toCSS)
				.filter((i) => i)
				.join('\n');
			this.response.push(`${DESKTOP_QUERY} { ${desktopCSS} }`);
		}
	}

	processAdStyle() {
		for (const { id, components } of this.articles) {
			if (!components) {
				continue;
			}
			const isAd = isArticleAnAd(components);
			if (isAd) {
				const css = [
					`#article-${id} .canvas {
                    overflow: hidden;
                    height: 100%;
                    max-height: 100%;
                }`,
				];
				css.push(`#article-${id} .canvas > div {
                    height: 100%;
                }`);
				this.response.push(css.join('\n'));
			}
		}
	}

	processTextStyle() {
		for (const { components } of this.articles) {
			if (!components) {
				continue;
			}
			calculateTextStyleComponent(components, this.response);
		}
	}

	processInlineStyles() {
		const stylesMap = this.stylesMap;
		for (const [, value] of Array.from(this.inlineStyleSelector)) {
			const { selector } = value;
			const styles: Array<Style> = value.styles
				.map((id) => {
					const style = stylesMap.get(`${id}`);
					if (style) {
						style.supportedDevices = new Set([
							...style.supportedDevices,
						]);
					}
					return style!;
				})
				.filter((s) => !!s);
			if (!styles.length) {
				continue;
			}

			const inlineStyles = {
				mobile: styles.filter((s) => s.supportedDevices.has('mobile')),
				tablet: styles.filter((s) => s.supportedDevices.has('tablet')),
				desktop: styles.filter((s) =>
					s.supportedDevices.has('desktop'),
				),
			};
			this.processInlineStyle(selector, inlineStyles);
		}
	}

	processInlineStyle = (
		selector: string,
		inlineStyles: {
			mobile: Array<Style>;
			tablet: Array<Style>;
			desktop: Array<Style>;
		},
	) => {
		let style: any;
		let virtualCSS: VirtualCSS;
		let response;
		// Process inline style on mobile
		if (inlineStyles.mobile.length) {
			style = StyleBuilder.mergeStyles(inlineStyles.mobile);
			virtualCSS = new VirtualCSS(style, selector);
			response = virtualCSS.getCSS();
			this.response.push(response);
		}
		// Process style in tablet
		if (inlineStyles.tablet.length) {
			style = StyleBuilder.mergeStyles(inlineStyles.tablet);
			virtualCSS = new VirtualCSS(style, selector);
			response = `${TABLET_QUERY} { ${virtualCSS.getCSS()} }`;
			this.response.push(response);
		}

		// Process inline style on desktop
		if (inlineStyles.desktop.length) {
			style = StyleBuilder.mergeStyles(inlineStyles.desktop);
			virtualCSS = new VirtualCSS(style, selector);
			response = `${DESKTOP_QUERY} { ${virtualCSS.getCSS()} }`;
			this.response.push(response);
		}
	};

	processCustomStyle() {
		const customStyles = Array.from(this.stylesMap.values())
			.filter(s => s.type === 'custom');
		for (const style of customStyles) {

			const virtualCSS = new VirtualCSS(style);

			const css = virtualCSS.getCSS();
			this.response.push(css);
		}
	}

	processArticlesComponentInlineStyles = () => {
		for (const article of this.articles) {
			const { id, components, styleProperties } = article;
			if (styleProperties) {
				this.processArticleStyleProperties(id, styleProperties);
			}
			this.processComponentInlineStyles(id, components);
		}
	};

	processArticleStyleProperties = (articleId: string, style: any) => {
		const { canvas } = style;
		if (canvas) {
			const { background_color, body_color } = canvas;
			if (body_color) {
				this.response.push(`#article-${articleId} {
                    background: #${body_color.replace('#', '')};
                }`);
			}
			if (background_color) {
				this.response
					.push(`#article-${articleId} .canvas > div:first-child {
                    background: #${background_color.replace('#', '')};
                }`);
			}
		}
	};

	processComponentInlineStyles = (
		articleId: string,
		components: Array<any>,
	) => {
		if (!components) {
			return;
		}

		for (const component of components) {
			if (
				component.component === 'image' &&
				component.styles &&
				component.styles.length
			) {
				this.processImageComponentInlineStyles(articleId, component);
				continue;
			}
			if (component.text && component.style) {
				this.processTextComponentInlineStyle(articleId, component);
				continue;
			}
			if (component.columns) {
				if (component.style) {
					this.processColumnsComponentInlineStyle(
						articleId,
						component,
					);
				}
				for (const column of component.columns) {
					this.processComponentInlineStyles(articleId, column);
				}
			}
		}
	};

	processTextComponentInlineStyle = (articleId: string, component: any) => {
		const { id, style, dropcap } = component;
		const { dropcap_font_family, dropcap_color } = style;
		const dropcapProperties = [];
		if (dropcap === 'on') {
			if (dropcap_color) {
				dropcapProperties.push(
					`color: #${dropcap_color.replace('#', '')} !important;`,
				);
			}

			if (dropcap_font_family) {
				dropcapProperties.push(
					`font-family: '${dropcap_font_family}' !important;`,
				);
			}
		}

		if (dropcapProperties.length) {
			this.response
				.push(`#article-${articleId} #${id} p:first-of-type:first-letter {
                ${dropcapProperties.join('\n')}
            }`);
		}
	};

	processImageComponentInlineStyles = (articleId: string, component: any) => {
		const stylesMap = this.stylesMap;
		const { id, styles } = component;
		if (!styles) {
			return;
		}

		const styleDevices = styles.reduce(
			(acc: any, styleId: any) => {
				const style = stylesMap.get(styleId);
				if (!style || !style?.properties?.image) {
					return acc;
				}

				const { supportedDevices } = style;
				if (!supportedDevices) {
					return acc;
				}
				const devices = [...supportedDevices];
				for (const device of devices) {
					acc[device] = StyleBuilder.mergeDeep(
						acc[device],
						style?.properties?.image,
					);
				}

				return acc;
			},
			{
				mobile: {},
				tablet: {},
				desktop: {},
			},
		);

		for (const device in styleDevices) {
			const deviceProperties = styleDevices[device];
			const {
				marginBottom,
				marginLeft,
				marginTop,
				marginRight,
				paddingLeft,
				paddingRight,
				paddingTop,
				paddingBottom,
				unit,
			} = deviceProperties;

			if (!unit) {
				return;
			}

			const properties: Array<string> = [];
			if (marginTop !== undefined) {
				properties.push(
					`margin-top: ${marginTop}${unit['marginTop'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (marginBottom !== undefined) {
				properties.push(
					`margin-bottom: ${marginBottom}${unit['marginBottom'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (marginLeft !== undefined) {
				properties.push(
					`margin-left: ${marginLeft}${unit['marginLeft'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (marginRight !== undefined) {
				properties.push(
					`margin-right: ${marginRight}${unit['marginRight'] ?? DEFAULT_UNIT
					};`,
				);
			}

			if (paddingTop !== undefined) {
				properties.push(
					`padding-top: ${paddingTop}${unit['paddingTop'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (paddingBottom !== undefined) {
				properties.push(
					`padding-bottom: ${paddingBottom}${unit['paddingBottom'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (paddingLeft !== undefined) {
				properties.push(
					`padding-left: ${paddingLeft}${unit['paddingLeft'] ?? DEFAULT_UNIT
					};`,
				);
			}
			if (paddingRight !== undefined) {
				properties.push(
					`padding-right: ${paddingRight}${unit['paddingRight'] ?? DEFAULT_UNIT
					};`,
				);
			}

			if (properties.length) {
				const css = `#article-${articleId} #${id}.image.media {
                    ${properties.join('\n')}
                }`;
				switch (device) {
					case 'tablet':
						this.response.push(`${TABLET_QUERY} {${css}}`);
						break;
					case 'desktop':
						this.response.push(`${DESKTOP_QUERY} {${css}}`);
						break;
					default:
						this.response.push(`${MOBILE_QUERY} {${css}}`);
						break;
				}
			}
		}
	};

	processColumnsComponentInlineStyle = (
		articleId: string,
		component: any,
	) => {
		const { id, background, style } = component;
		const { background_color, colors } = style;
		const properties = [];

		/*if (background === 'none') {
			properties.push(`background: none;`);
		}*/

		if (background === 'solid' && background_color) {
			properties.push(
				`background: #${background_color.replace('#', '')};`,
			);
		}

		if (background === 'gradient' && colors?.length) {
			const total = style.colors.reduce((acc: any, item: any) => {
				const { weight } = item;
				acc += parseInt(`${weight}`);
				return acc;
			}, 0);
			let gradientAcc = 0;
			const linearGradient = style.colors.map((item: any) => {
				const { color, weight } = item;
				const totalPercent = (100 * parseFloat(weight)) / total;
				gradientAcc += totalPercent;
				return `#${color.replace(`#`, '')} ${gradientAcc}%`;
			});
			const gradientAngle = component.gradientangle || 180;
			properties.push(
				`background-image: linear-gradient(${gradientAngle}deg,${linearGradient.join()});`,
			);
		}

		if (properties.length) {
			this.response.push(`#article-${articleId} #${id} {
                ${properties.join('\n')}
            }`);
		}
	};

	toStyle = (id: string): Style | undefined => {
		return this.stylesMap.get(id);
	};

	loadStylesFromArticle() {
		this.addInheritenceStyle();
		for (const { id, style, components } of this.articles) {
			const articleStyle = this.stylesMap.get(`${style}`);
			if (!articleStyle) {
				continue;
			}
			const classNames = [];
			this.styles.mobile.add(style);
			classNames.push(`style-${style}`);
			const { desktop, tablet } = articleStyle;
			if (desktop && this.stylesMap.has(`${desktop}`)) {
				this.styles.desktop.add(desktop);
				classNames.push(`style-${desktop}`);
			}
			if (tablet && this.stylesMap.has(`${tablet}`)) {
				this.styles.tablet.add(tablet);
				classNames.push(`style-${tablet}`);
			}

			this.classNameMap.set(id, [...new Set([...classNames])].join(' '));
			calculateInlineStyle(
				components,
				this.inlineStyleSelector,
				this.response,
			);
		}
	}

	addInheritenceStyle = () => {
		this.articles = this.articles.map(clone).map((article) => {
			if (article.components) {
				article.components = article.components.reduce(
					this.reduceColumnInheritance(null),
					[],
				);
			}
			return article;
		}, []);
	};

	reduceColumnInheritance(parent: null | string) {
		return (acc: Array<any>, component: any) => {
			if (parent) {
				if (!component.parent) {
					component.parent = new Set();
				}
				component.parent.add(parent);
			}
			if (component.component === 'columns') {
				component.columns = component.columns.map(
					(column: Array<any>) =>
						column.reduce(
							this.reduceColumnInheritance(component.id),
							[],
						),
				);
			}
			acc.push(component);
			return acc;
		};
	}
}

function toCSS(style?: Style): string {
	if (!style) {
		return '';
	}
	const css = new VirtualCSS(style);
	const response = css.getCSS();
	return response;
}

function calculateTextStyleComponent(
	components: Array<any>,
	response: Array<any>,
) {
	if (!components) {
		return;
	}
	for (const component of components) {
		const { id, imagefloat, imagemargin, imagewidth } = component;
		if (textComponentsSet.has(component.component)) {
			const properties = [];
			if (imagefloat) {
				properties.push(`float: ${imagefloat};`);
			}
			if (imagewidth) {
				properties.push(`width: ${imagewidth}px;`);
			}
			if (imagemargin) {
				properties.push(`margin: ${imagemargin}px`);
			}
			if (!properties.length) {
				continue;
			}
			response.push(`#${id} figure{
                ${properties.join('\n')}
            }`);

			response.push(`#${id} img{
                width: 100%;
            }`);
		}
		if (component.component === 'columns') {
			for (const column of component.columns) {
				calculateTextStyleComponent(column, response);
			}
		}
	}
}

function calculateInlineStyle(
	components: Array<any> | null,
	inlineStylesComponent: Map<string, InlineStyleValueSelector>,
	response: Array<any>,
) {
	if (!components) {
		return;
	}
	for (const component of components) {
		if (component.component === 'columns') {
			const {
				id,
				parent,
				styles,
				columns,
				colsplit,
				gutter,
				collapsetype,
			} = component;
			const total = columns.length;
			const css = `#${id} > :first-child {
                display: grid;
                grid-template-columns: ${getColSplit(total, colsplit)};
                grid-template-rows: 1fr;
                gap: 0px ${gutter !== undefined ? gutter : 0}px;
                grid-template-areas:
                "${columns.map(() => '.').join(' ')}";
            }`;
			const collapseCSS = [
				`#${id} > :first-child {`,
				'display: flex;',
				'flex-direction: column;',
				'}',
			].join('\n');
			response.push(css);
			if (collapsetype === 'responsive') {
				response.push(`${MOBILE_QUERY} { ${collapseCSS} }`);
			} else if (collapsetype === 'tablet') {
				response.push(`${TABLET_QUERY} { ${collapseCSS} }`);
			}
			if (parent) {
				parent.add(id);
			}
			if (styles && styles.length) {
				const selector = parent
					? [...parent].map((c) => `#${c}`).join(' ')
					: `#${id}`;

				inlineStylesComponent.set(id, {
					selector,
					styles,
				});
			}

			for (const column of component.columns) {
				calculateInlineStyle(column, inlineStylesComponent, response);
			}
		}
	}
}

function getColSplit(total: number, colSplit: any) {
	if (total === 1) {
		return '1fr';
	}

	return colSplit
		.split('-')
		.map((split: any) => `${split}fr`)
		.join(' ');
}

function isArticleAnAd(components: Array<any>) {
	return components?.length === 1 && components[0].component === 'advert';
}
